// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type EsgScores } from '@advisor-app/features/esg';
import { numberToFixed, classNames } from '@shared/ui-kit/utils';
import {
  AssetBrand,
  ChartContainer,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

interface TopCompaniesTableProps {
  assetsEsg: TopCompanyEsg[];
}

interface TopCompanyEsg extends EsgScores {
  ticker: string;
  name: string;
  url_logo: string;
  weight: number;
  url: string;
}

interface CompanyRowProps {
  company: TopCompanyEsg;
}

interface ScoreLightProps {
  score: number;
}

const ScoreLight: FC<ScoreLightProps> = ({ score }) => {
  return (
    <div className="flex items-center justify-around">
      <div className="!money-display">{numberToFixed(score * 100, 0)}</div>
      <div
        className={classNames('!money-display size-3 rounded-full', {
          'bg-esg-low/50': score > 0 && score < 0.5,
          'bg-esg-mid/50': score >= 0.5 && score < 0.8,
          'bg-esg-high': score >= 0.8,
        })}
      />
    </div>
  );
};

const CompanyRow: FC<CompanyRowProps> = ({ company }) => {
  const { name, weight, url_logo, url } = company;
  const { environmentScore, governanceScore, socialScore } = company;
  return (
    <tr role="row" className="[&>td]:py-2">
      <td>
        <AssetBrand
          ticker={name}
          assetType="stock"
          label={name}
          logoUrl={url_logo}
          url={url}
          watchlistButton={
            <WatchlistToggleButton ticker={name} assetType="stock" />
          }
        />
      </td>
      <td className="!money-display pr-2 text-right">{`${numberToFixed(
        weight * 100,
        1,
      )}%`}</td>
      <td className="!money-display text-right">
        <ScoreLight score={environmentScore} />
      </td>
      <td className="!money-display text-right">
        <ScoreLight score={socialScore} />
      </td>
      <td className="!money-display text-right">
        <ScoreLight score={governanceScore} />
      </td>
    </tr>
  );
};

export const TopEsgCompanies = ({ assetsEsg }: TopCompaniesTableProps) => {
  const { t } = useTranslation();
  return (
    <ChartContainer
      header={t('research.top-10-companies-esg-ratings', { ns: 'research' })}
    >
      <table className="text-primary-medium" data-testid="top-10-companies-esg">
        <thead>
          <tr className="[&>th]:pb-2 [&>th]:font-normal">
            <th>{t('common.name', { ns: 'common' })}</th>
            <th className="pr-2 text-right">
              {t('common.percent-of-total', { ns: 'common' })}
            </th>
            <th className="text-center">E</th>
            <th className="text-center">S</th>
            <th className="text-center">G</th>
          </tr>
        </thead>
        <tbody>
          {assetsEsg.map(assetEsg => (
            <CompanyRow key={assetEsg.ticker} company={assetEsg} />
          ))}
        </tbody>
      </table>
    </ChartContainer>
  );
};
